
import { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { IStore } from '../../interfaces';
import { EnumSection, IScarfUpdate } from '../../interfaces/clientInterfaces';
import * as actions from '../../actions';
import { LoadingIcon, LoadingIconContainer, LoadingIconDesc } from './SideTree';
import { useAppDispatch } from '../../utils/modules';
import styles from './Home.module.css';

export default function Home() {

	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(actions.setSectionName(EnumSection.home));
	}, [dispatch]);

	const scrollDivRef = useRef<HTMLDivElement>(null);

	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => {
			return !!completeState?.ui?.isMobileViewActive;
		});

	const updates = useSelector(
		(completeState: IStore): IScarfUpdate[] => {
			return completeState?.scarves?.lastScarves || [];
		});

	const renderUpdateScarf = useCallback(
		(
			scarfUpdate: IScarfUpdate,
			index: number
		) => {
			const dateString = scarfUpdate.date.toLocaleString('default', { month: 'long' }) + '/' + scarfUpdate.date.getFullYear();
			return (
				<div
					className={styles.updateContainer}
					key={index}
					onClick={() => dispatch(actions.setHomeDetailTeam(scarfUpdate.teamId))}
				>
					<div className={styles.nameDescription}>
						{!scarfUpdate.isMatch && <img
							className={styles.flagOrLogoImage}
							src={`../../../img/flag/${scarfUpdate.countryOrCompetitionCode}.png`}
							alt={scarfUpdate.name}
						/>}
						<div>{scarfUpdate.name + ' - ' + dateString}</div>
					</div>
					<img
						className={styles.homeUpdateImage}
						style={{
							width: isMobileViewActive ? '90%' : '600px'
						}}
						src={`../../../img/scarf/${scarfUpdate.code}.jpg`}
						alt={`${scarfUpdate.name} scarf`}
						title={`${scarfUpdate.name} scarf`}
					/>
				</div>
			);
		},
		[dispatch, isMobileViewActive]
	);

	const scrollToContainer = useCallback(
		() => {
			if (scrollDivRef.current) {
				scrollDivRef.current.scrollIntoView();
			}
		},
		[]
	);

	return (
		<>
			<Helmet>
				<title>{'Football scarf collection´s home - FootballScarfMuseumCollection'}</title>
				<link rel='canonical' href='https://www.scarfmuseumflavio.com' />
				<meta http-equiv='Content-Type' content='text/html; charset=utf-8' />
				<meta name='description' content='Football scarf collection from all over the world. Football club trophies and titles shown museum alike.' />
				<meta name='viewport' content='width=device-width, initial-scale=1.0'/>
			</Helmet>
			<div
				className={styles.homeContainer}
				style={{
					background: 'url(\'../img/background/home.jpg\') no-repeat center center fixed',
					backgroundSize: 'cover'
				}}
			>
				<div className={styles.homeGalleryContainer}>
					<div
						className={styles.homeIntroDescriptionContainer}
					>
						<p>{'Welcome to my scarves´ museum site. My name is Álvaro, from Spain but living in Austria, and here you can find all the scarves I have been collecting for a few years now.'}</p>
					</div>
					<div className={styles.readMoreButton} onClick={scrollToContainer}>
						<i className='fa fa-caret-down'/>	
						<div>{'READ MORE'}</div>
						<i className='fa fa-caret-down'/>	
					</div>
					<div className={styles.updatesContainer} >
						<div className={styles.homeUpdatesTitle}>{'NEWEST SCARVES'}</div>
						{
							updates.length > 0
								? updates.map((x, i) => renderUpdateScarf(x, i))
								: <LoadingIconContainer>
									<LoadingIcon/>
									<LoadingIconDesc>{'LOADING SCARVES...'}</LoadingIconDesc>
								</LoadingIconContainer>

						}
					</div>
					<div
						className={styles.longDescriptionContainer}
						ref={scrollDivRef}
					> 
						<p>{'It all started on a trip to Lisbon with my girldfriend, where I found serious troubles deciding between a scarf from one of the three main clubs of the city (Benfica, Belenenses and Sporting) to take back home as a souvenir. As the day to come back home was approaching, the time to take a decision arrived, and I finally opted to take the three scarves with me, which at the same time triggered the collector in me.'}</p>
						<p>{'There was a point where the collection grew and grew, and I came up with the necessity of having a place to exhibit all the pieces of the collection. As the plans for a physical museum seem for the moment far away, I took advantage of the lockdown produced by the Covid-19 pandemic to start developing this site.'}</p>
						<p>{'As you may experience, this is not a regular collection site, but a site where you can find some info about the clubs present in my collection, as well as the relevant highlights of their history, and importance in their country, conference and whole world thanks to the available filters.'}</p>
						<p>{'You can as well search for your team in the map, or take a geographical look at each country or region. Do not forget to visit the Exchange site, where you will find further details about exchanging scarves.'}</p>
						<p>{'Contact mail: '}<em><a className={styles.exchangeMail} href="mailto:exchange@scarfmuseumflavio.com">exchange@scarfmuseumflavio.com</a></em></p>
					</div>
				</div>
			</div>
		</>	
	);
}

